import { connect } from "dva";
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Button, Checkbox, Input, Modal, Progress, Slider, message } from "antd";
import "./index.less";
import AttachmentComp from "@/components/AttachmentComp/handleAttachment";
import { NewTaskModel, PROCESS_STATUS, TASK_STATUS } from "@/types/newWork";
import { nanoid } from "nanoid";
import { TaskResourceBizType, TaskResourcePermissionType } from "@/types/work";
import { analysisLog } from "@/utils/logAnalytics";
import bus from "@/utils/bus";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { BusinessForm } from "@/utils/chat/message";

export enum FileStatus {
  Init = 1,
  Uploading,
  Done,
  failed
}

// 进度条+滑动输入条
export function ProgressSliderCOMPLEX({
  max, // 个人权重数：整数
  current, // 百分比，如 10， 代表用户进度为 10%
  totalWeight,
  setter,
  className = "",
  isDisabled = false,
  maxpercent = 100
}: {
  max: number;
  maxpercent?: number;
  current: number;
  totalWeight: number;
  setter: Function;
  className: string;
  isDisabled?: boolean;
}) {
  let initialVal = current;
  const [val, setVal] = useState(current);
  useEffect(() => {
    setVal(current > maxpercent ? maxpercent : current);
  }, [current]); // eslint-disable-line
  const onChange = (v: any) => {
    setVal(v);
    setter(v);
  };
  const sliderRef: any = useRef(null);
  return (
    <div className={["lb-however", className].join(" ")} ref={sliderRef}>
      <div className={"progress-slider-complex" + (val !== current ? " changed" : "")}>
        <Slider
          value={val}
          max={100}
          className="progress-slider-comp"
          tipFormatter={(v) => (
            <span className={isDisabled ? "tooltip-color-disabled" : ""}>{v}%</span>
          )}
          onAfterChange={() => {
            analysisLog("xtbg_add_task_1715330698182", "13");
          }}
          tooltipVisible={true}
          tooltipPlacement="bottom"
          onChange={onChange}
          disabled={isDisabled}
          getTooltipPopupContainer={(trigger) => {
            return trigger.parentNode?.parentNode as HTMLElement;
          }}
        />
        <Progress percent={initialVal} size="small" showInfo={false} />
      </div>
    </div>
  );
}

// eslint-disable-next-line complexity
function TaskProcessModal({
  task,
  isHead,
  executor,
  setUpdateProcessVisible,
  updateProcessAction,
  uploadControlName
}: {
  task: NewTaskModel | null;
  isHead: boolean; // 是否是任务创建人或负责人
  executor: any; //  任务协同人
  setUpdateProcessVisible: Function;
  updateProcessAction: Function;
  uploadControlName: string;
}) {
  const [taskStatus, setTaskStatus] = useState(task?.taskStatus || -1);
  const [comment, setComment] = useState("");
  const [taskComment, setTaskComment] = useState("");
  const [progressMy, setProgressMy] = useState(task?.progress || 0);
  const [processStatus, setProcessStatus] = useState<PROCESS_STATUS | -1>(
    task?.processStatus || -1
  );
  const [fileListOfSelfProgress, setFileListOfSelfProgress] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const textareaRef: any = useRef(null);
  const { t } = useTranslation("work");
  const location = useLocation();

  const onSelfAttachmentRemove = (fileId: string) => {
    const index = fileListOfSelfProgress.findIndex((item) => item.bizId === fileId);
    if (index < -1) {
      return;
    }
    const temp = fileListOfSelfProgress.map((item) => {
      return {
        ...item,
        fileName: item.name,
        fileId: item.bizId || nanoid(20),
        fileUrl: item.url,
        status: FileStatus.Done
      };
    });
    temp.splice(index, 1);
    setFileListOfSelfProgress([...temp] as any);
  };

  const onSelfAttachmentUploadFinish = (file: any) => {
    analysisLog("xtbg_add_task_1715330698182", "14");
    if (Array.isArray(file)) {
      const files = file;
      let newArr = files.map((f) => {
        return {
          ...f,
          name: f.fileName,
          size: f.size,
          url: f.fileUrl,
          resourceId: nanoid(),
          bizId: f.fileId,
          bizType: f.bizType || TaskResourceBizType.FILE,
          resourceType: TaskResourcePermissionType.COMMON,
          fileType: f.fileType,
          creatorUserId: f?.creatorUserId
        };
      });
      const resources = fileListOfSelfProgress;
      resources.push(...newArr);
      resources.forEach((item) => {
        Object.assign(item, {
          fileName: item.name,
          fileId: item.bizId || nanoid(20),
          fileUrl: item.url,
          status: FileStatus.Done
        });
      });

      setFileListOfSelfProgress([...resources] as any);
      return Promise.resolve(newArr);
    }
    let newFile = {
      name: file.fileName,
      size: file.size,
      url: file.fileUrl,
      resourceId: nanoid(),
      bizId: file.fileId,
      bizType: file.bizType || TaskResourceBizType.FILE,
      resourceType: TaskResourcePermissionType.COMMON,
      fileType: file.fileType,
      creatorUserId: file?.creatorUserId
    };
    const resourceId = nanoid();

    const temp = {
      ...file,
      ...newFile,
      percent: 0,
      fileUrl: file.fileUrl,
      resourceId,
      status: FileStatus.Done
    };
    // 移除不必要的字段，否则网路请求会报错
    delete temp.icon;
    delete temp.taskInstance;

    fileListOfSelfProgress.push(temp);
    setFileListOfSelfProgress([...fileListOfSelfProgress] as any);
  };

  const handleClose = () => {
    setTaskComment("");
    setComment("");
    setFileListOfSelfProgress([]);
    setUpdateProcessVisible(false);
  };

  useEffect(() => {
    if (executor && executor.processAuditStatus === 2) {
      if (executor.processAttachments) {
        setFileListOfSelfProgress(executor.processAttachments);
      }
      if (executor.afterProcessStatus) {
        setProcessStatus(executor.afterProcessStatus);
      }
      if (executor.afterProgress) {
        setProgressMy(executor.afterProgress || 0);
      }
      if (executor.processComments) {
        setComment(executor.processComments);
      }
    }
  }, [executor]);

  useEffect(() => {
    if (taskStatus === TASK_STATUS.ABORT && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [taskStatus]);

  if (!task) {
    return null;
  }

  return (
    <Modal
      className="task-update-progress"
      title={isHead ? "更新状态" : "更新进展"}
      centered
      visible={true}
      onCancel={() => {
        analysisLog("xtbg_add_task_1715330698182", "15");
        handleClose();
      }}
      width={500}
      footer={
        <>
          <Button
            onClick={() => {
              analysisLog("xtbg_add_task_1715330698182", "15");
              handleClose();
            }}
          >
            取消
          </Button>
          <Button
            type="primary"
            loading={submitting}
            disabled={!isHead && executor && executor.processAuditStatus === 0 ? true : false}
            onClick={() => {
              analysisLog("xtbg_add_task_1715330698182", "16");
              if (!(isHead || executor)) {
                return message.error(t("Only edit task progress"));
              }
              // 未收到返回结果之前要防止按钮重复点击
              setSubmitting(true);
              let params: any = {
                taskId: task?.taskId
              };
              if (isHead) {
                if (taskStatus === TASK_STATUS.ABORT && !taskComment) {
                  setSubmitting(false);
                  return message.error("请填写情况说明");
                }
                params = { ...params, ...{ taskStatus: taskStatus, taskComment: taskComment } };
              }
              if (executor) {
                params = {
                  ...params,
                  ...{
                    comment: comment,
                    processStatus: processStatus,
                    progress: progressMy,
                    taskRecordAttachments: fileListOfSelfProgress
                  }
                };
              }
              updateProcessAction(params, () => {
                if (
                  params?.hasOwnProperty("taskStatus") &&
                  location.pathname?.indexOf("/calendar") > -1
                ) {
                  bus.emit("calendar:reloadSchedule");
                }
                setSubmitting(false);
                handleClose();
              });
            }}
          >
            确认更新
          </Button>
        </>
      }
    >
      <div>
        {isHead && (
          <div className="task-progress">
            <div className="task-progress-status">
              <span className="check-btn">
                <Checkbox
                  className={`task-status-${TASK_STATUS.FINISHED}`}
                  style={{ marginRight: "50px" }}
                  checked={taskStatus === TASK_STATUS.FINISHED}
                  onChange={(e: any) => {
                    analysisLog("xtbg_add_task_1715330698182", "9");
                    if (e?.target?.checked) {
                      setProgressMy(100);
                      setProcessStatus(PROCESS_STATUS.FINISHED);
                      setTaskStatus(TASK_STATUS.FINISHED);
                    } else {
                      setProgressMy(
                        executor && executor.processAuditStatus === 2
                          ? executor.afterProgress || 0
                          : task?.progress
                      );
                      setProcessStatus(task?.processStatus || -1);
                      setTaskStatus(TASK_STATUS.UNDONE);
                    }
                  }}
                >
                  任务完成
                </Checkbox>
                <Checkbox
                  className={`task-status-${TASK_STATUS.ABORT}`}
                  checked={taskStatus === TASK_STATUS.ABORT}
                  onChange={(e: any) => {
                    analysisLog("xtbg_add_task_1715330698182", "10");
                    if (e?.target?.checked) {
                      setTaskStatus(TASK_STATUS.ABORT);
                    } else {
                      setTaskStatus(TASK_STATUS.UNDONE);
                    }
                  }}
                >
                  任务中止
                </Checkbox>
              </span>
              {(taskStatus === TASK_STATUS.FINISHED ||
                taskStatus === TASK_STATUS.ABORT ||
                !executor) && (
                <div className={`textarea ${!executor ? "textarea_onlyhead" : ""}`}>
                  <Input.TextArea
                    ref={textareaRef}
                    placeholder={
                      taskStatus === TASK_STATUS.ABORT ? "情况说明(中止任务必填)" : "情况说明"
                    }
                    rows={3}
                    // defaultValue={(executor as any).processAuditStatus === 2 && (executor as any).processComments}
                    value={taskComment}
                    onChange={(e) => {
                      e.stopPropagation();
                      setTaskComment(e.target.value);
                    }}
                    maxLength={500}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {isHead &&
          executor &&
          (taskStatus === TASK_STATUS.FINISHED || taskStatus === TASK_STATUS.ABORT) && (
            <div className="progress-split-line" />
          )}
        {executor && (
          <div className="personal-progress">
            {/* 驳回个人进展申请 */}
            {(executor as any)?.processAuditStatus !== null &&
              (executor as any)?.processAuditStatus !== 1 && (
                <div className="personal-progress-status-tip">
                  {(executor as any)?.processAuditStatus === 0
                    ? "您有一条任务进度审批中，审批完成后方可再次提交新的进度。"
                    : (executor as any)?.auditComments}
                </div>
              )}
            <div className={`personal-progress-status task-progress-status-${taskStatus}`}>
              <div className="status-check">
                <label>您的个人进展</label>
                <span>
                  <Checkbox
                    className={`process-status-${PROCESS_STATUS.DIFFICULT}`}
                    style={{ marginRight: "30px" }}
                    disabled={
                      taskStatus === TASK_STATUS.FINISHED || taskStatus === TASK_STATUS.ABORT
                    }
                    checked={processStatus === PROCESS_STATUS.DIFFICULT}
                    onChange={(e: any) => {
                      analysisLog("xtbg_add_task_1715330698182", "11");
                      if (e?.target?.checked) {
                        setProcessStatus(PROCESS_STATUS.DIFFICULT);
                      } else {
                        setProcessStatus(PROCESS_STATUS.PUSHING);
                      }
                    }}
                  >
                    有困难
                  </Checkbox>
                  <Checkbox
                    className={`process-status-${PROCESS_STATUS.FINISHED}`}
                    disabled={
                      taskStatus === TASK_STATUS.FINISHED || taskStatus === TASK_STATUS.ABORT
                    }
                    checked={processStatus === PROCESS_STATUS.FINISHED}
                    onChange={(e: any) => {
                      analysisLog("xtbg_add_task_1715330698182", "12");
                      if (e?.target?.checked) {
                        setProgressMy(100);
                        setProcessStatus(PROCESS_STATUS.FINISHED);
                      } else {
                        setProgressMy(
                          executor && executor.processAuditStatus === 2
                            ? executor.afterProgress || 0
                            : task?.progress
                        );
                        setProcessStatus(PROCESS_STATUS.PUSHING);
                      }
                    }}
                  >
                    完成
                  </Checkbox>
                </span>
              </div>
              <ProgressSliderCOMPLEX
                className={`related-task-status-${taskStatus} related-process-status-${processStatus}`}
                max={task?.weight || 0}
                current={
                  processStatus === 2
                    ? Infinity
                    : (executor as any)?.processAuditStatus === 2
                    ? (executor as any)?.afterProgress
                    : task?.progress || 0
                }
                totalWeight={task?.totalWeight || 100}
                isDisabled={taskStatus === TASK_STATUS.FINISHED || taskStatus === TASK_STATUS.ABORT}
                setter={(v: number) => {
                  setProgressMy(v);
                  if (v === 100) {
                    setProcessStatus(PROCESS_STATUS.FINISHED);
                  } else if (processStatus === PROCESS_STATUS.FINISHED) {
                    setProcessStatus(PROCESS_STATUS.PUSHING);
                  }
                }}
              />
            </div>
            <div className="textarea">
              <Input.TextArea
                placeholder="请输入推进具体事项"
                rows={3}
                disabled={taskStatus === TASK_STATUS.FINISHED || taskStatus === TASK_STATUS.ABORT}
                defaultValue={
                  (executor as any)?.processAuditStatus === 2 && (executor as any)?.processComments
                }
                value={comment}
                onChange={(e) => {
                  e.stopPropagation();
                  setComment(e.target.value);
                }}
                maxLength={500}
              />
            </div>
            <div className="files-of-progress-wrap">
              <nav>上传附件</nav>
              <AttachmentComp
                businessForm={BusinessForm.TASK}
                disabled={false}
                readOnly={taskStatus === TASK_STATUS.FINISHED || taskStatus === TASK_STATUS.ABORT}
                mode="create"
                uploaderProxy={false}
                multiple={true}
                onRemove={onSelfAttachmentRemove}
                onFinish={onSelfAttachmentUploadFinish}
                fileList={fileListOfSelfProgress}
                removeSkipConfirm={true}
                uploadControlName={uploadControlName}
                bizId={task?.taskId}
              >
                <p style={{ fontStyle: "normal" }}>+添加附件</p>
              </AttachmentComp>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default TaskProcessModal;
